import { keyframes } from "@emotion/react";
import { Box, Image, Theme, Typography } from "@outschool/backpack";
import {
  HOMEPAGE_PREVIEW_IMAGE,
  imageUrl as getImageUrl,
} from "@outschool/filestack-urls";
import {
  SUPPORTED_LOCALES,
  Trans,
  useTranslation,
} from "@outschool/localization";
import { QuizVariant, onboardingQuizPath, pathToUrl } from "@outschool/routes";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Loading, TrackingPage } from "@outschool/ui-legacy-component-library";
import { lazyWithRetry, useIsMobile, useNavigation } from "@outschool/ui-utils";
import React from "react";
import { Helmet } from "react-helmet";

import HeroImage1 from "../../images/homepage/home_hero/bts_campaign/bts_hero_image_1.webp";
import HeroImage2 from "../../images/homepage/home_hero/bts_campaign/bts_hero_image_2.webp";
import HeroImage3 from "../../images/homepage/home_hero/bts_campaign/bts_hero_image_3.webp";
import HeroImage4 from "../../images/homepage/home_hero/bts_campaign/bts_hero_image_4.webp";
import HeroImage5 from "../../images/homepage/home_hero/bts_campaign/bts_hero_image_5.webp";
import HeroImage6 from "../../images/homepage/home_hero/bts_campaign/bts_hero_image_6.webp";
import ArrowImageMobile from "../../images/homepage/home_hero/bts_campaign/purple_arrow_m.webp";
import ArrowImage from "../../images/homepage/home_hero/bts_campaign/purple_arrow.webp";
import { MAIN_IMAGE_HEIGHT, MAIN_IMAGE_WIDTH } from "../../shared/Activity";
import SimpleLayout from "../components/layouts/SimpleLayout";
import { clearOnboardingState } from "../components/onboarding/sessionStorageUtils";

const BelowTheFoldLoggedOutUserHome = lazyWithRetry(
  /* webpackChunkName: "BelowTheFoldLoggedOutUserHome" */
  () => import("../components/BelowTheFoldLoggedOutUserHome"),
);

function RotatingKids({
  kidImageUrls,
  pauseSeconds,
  transitionSeconds,
}: {
  kidImageUrls: string[];
  pauseSeconds: number;
  transitionSeconds: number;
}) {
  const timePerImage = pauseSeconds + transitionSeconds;
  const totalDuration = kidImageUrls.length * timePerImage;

  const toPercent = (time: number) => (time / totalDuration) * 100;
  const fadeOutStart = toPercent(pauseSeconds);
  const fadeOutEnd = toPercent(pauseSeconds + transitionSeconds);
  const fadeInStart = 100 - toPercent(transitionSeconds);

  const swap = keyframes`
                  0% { opacity: 1; }
    ${fadeOutStart}% { opacity: 1; } 
    ${fadeOutEnd}%   { opacity: 0; }
    ${fadeInStart}%  { opacity: 0; }
                100% { opacity: 1; }`;

  return (
    <Box
      sx={(theme: Theme) => ({
        flexShrink: 0,
        width: "560px",
        height: "626px",
        position: "relative",
        zIndex: 1,
        overflow: "hidden",
        display: "inline-flex",
        alignItems: "center",
        gridArea: "image",
        marginTop: "5.5rem",
        marginLeft: "-1.4rem",
        [theme.breakpoints.down("md")]: {
          marginTop: "0",
          marginLeft: "0",
          width: "300px",
          height: "336px",
        },
        [theme.breakpoints.only("md")]: {
          marginTop: "24rem",
          marginLeft: "-3rem",
        },
      })}
    >
      {[...new Set(kidImageUrls)].map((url, idx) => (
        <Image
          alt={`image of kid ${idx}`}
          key={url}
          src={url}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            animation: `${swap} ${totalDuration}s ease-in-out infinite`,
            animationDelay: `${idx * timePerImage - totalDuration}s`,
            opacity: 0, // Set initial opacity to 0 to prevent all images showing at once
          }}
          sizes={`(max-width: 768px) 80vw, 50vw`}
        />
      ))}
    </Box>
  );
}

function PurpleArrow() {
  const isMobile = useIsMobile();
  return (
    <Image
      alt={`purple arrow`}
      src={isMobile ? ArrowImageMobile : ArrowImage}
      sx={(theme: Theme) => ({
        width: "364px",
        objectFit: "contain",
        [theme.breakpoints.down("sm")]: {
          alignSelf: "center",
        },
        [theme.breakpoints.up("md")]: {
          position: "absolute",
          transform: "rotate(2.83deg)",
          left: "2rem",
          bottom: "1rem",
          width: "464px",
        },
        [theme.breakpoints.up("lg")]: {
          left: "16rem",
          bottom: "0",
        },
      })}
    />
  );
}

function LoggedOutUserHero() {
  const { t } = useTranslation("client\\components\\NewUserHome");

  const heroKidImages = [
    HeroImage1,
    HeroImage2,
    HeroImage3,
    HeroImage4,
    HeroImage5,
    HeroImage6,
  ];
  const navigation = useNavigation();
  const isMobile = useIsMobile();

  return (
    <Box
      sx={{
        position: "relative",
      }}
    >
      <Box
        sx={(theme: Theme) => ({
          marginX: "auto",
          maxWidth: "1120px",
          [theme.breakpoints.up("lg")]: {
            marginBottom: "11rem",
          },
        })}
      >
        <Box
          flex
          sx={(theme: Theme) => ({
            position: "relative",
            flexDirection: "column-reverse",
            alignItems: "center",
            paddingTop: "3.3rem",
            [theme.breakpoints.up("md")]: {
              flexDirection: "row",
              justifyContent: "space-around",
              alignItems: "start",
              paddingBottom: "1.4rem",
            },
            [theme.breakpoints.up("lg")]: {
              justifyContent: "space-between",
            },
          })}
        >
          <Box
            sx={(theme: Theme) => ({
              position: "relative",
              display: "inline-flex",
              flexDirection: "column",
              gap: "1em",
              alignItems: "center",
              width: "100%",
              textAlign: "center",
              zIndex: 2,
              [theme.breakpoints.down("sm")]: {
                textAlign: "left",
                alignItems: "start",
                paddingX: "2rem",
                marginBottom: "8rem",
              },
              [theme.breakpoints.up("md")]: {
                width: "406px",
                paddingLeft: "2rem",
                textAlign: "left",
                alignItems: "start",
              },
              [theme.breakpoints.up("lg")]: {
                alignItems: "start",
                width: "640px",
                textAlign: "left",
                paddingTop: "5.4rem",
                marginTop: theme.spacing(19),
              },
            })}
          >
            <Box
              sx={(theme: Theme) => ({
                display: "flex",
                flexDirection: "column",
                gap: "2.8rem",
                [theme.breakpoints.down("md")]: {
                  gap: "1.8rem",
                },
              })}
            >
              <Typography
                emphasized={true}
                sx={(theme: Theme) => ({
                  position: "relative",
                  typography: "display1",
                  color: theme.palette.warning.main,
                  fontSize: "6.4rem",
                  lineHeight: 0.9,
                  maxWidth: "320px",
                  [theme.breakpoints.up("md")]: {
                    fontSize: "12.6rem",
                    maxWidth: "620px",
                  },
                })}
              >
                <Trans t={t}>Grow Beyond</Trans>
              </Typography>
              <Typography
                sx={(theme: Theme) => ({
                  color: theme.palette.primary.main,
                  typography: "display1",
                  [theme.breakpoints.down("md")]: {
                    typography: "display2",
                  },
                })}
              >
                <Trans t={t}>the Classroom</Trans>
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={(theme: Theme) => ({
                fontSize: "1.8rem",
                maxWidth: "435px",
                [theme.breakpoints.down("sm")]: {
                  maxWidth: "none",
                },
              })}
            >
              <Trans t={t}>
                Live online small-group classes and 1:1 tutoring led by real
                teachers, designed to spark curiosity and build real-world
                skills.
              </Trans>
            </Typography>
            <TrackedButton
              trackingName="onboarding-quiz-get-started-button"
              variant="contained"
              sx={(theme: Theme) => ({
                marginTop: "1.5em",
                paddingY: "1.3rem",
                [theme.breakpoints.up("md")]: {
                  alignSelf: "start",
                },
              })}
              onClick={() => {
                clearOnboardingState(); // Clear onboarding state before navigating to quiz
                navigation(
                  onboardingQuizPath(undefined, QuizVariant.GetStarted),
                  { hardNav: true },
                );
              }}
            >
              <Typography variant="body1" sx={{ fontSize: "1.8rem" }}>
                {t("Get Started")}
              </Typography>
            </TrackedButton>
            {isMobile && <PurpleArrow />}
          </Box>
          <RotatingKids
            kidImageUrls={heroKidImages}
            pauseSeconds={3}
            transitionSeconds={0.3}
          />
          {!isMobile && <PurpleArrow />}
        </Box>
      </Box>
    </Box>
  );
}

export default function LoggedOutUserHome({ ...props }) {
  const [triggerLoaded, setTriggerLoaded] = React.useState(false);
  React.useEffect(() => {
    setTriggerLoaded(true);
  }, []);

  return (
    <TrackingPage
      impressionEventName={"Home Page Impression"}
      uniqueId="homePage"
      name="homePage"
      useLinkTracking
    >
      {setImpressionNode => (
        <SimpleLayout
          ref={node => {
            setImpressionNode(node!);
          }}
          {...props}
          allowCategoriesNavbar
        >
          <Box>
            <LoggedOutUserHero />
            {triggerLoaded ? (
              <React.Suspense fallback={<Loading />}>
                <BelowTheFoldLoggedOutUserHome />
              </React.Suspense>
            ) : null}
          </Box>
        </SimpleLayout>
      )}
    </TrackingPage>
  );
}

export function HomePageMetaTags() {
  const { t } = useTranslation("client\\components\\NewUserHome");
  const canonicalUrl = pathToUrl("/");
  const title = t(
    "Get affordable online classes and tutoring for kids at Outschool",
  );
  const description = t(
    "Find the perfect academic resource for your kids. Try online classes and tutoring from Outschool’s vetted 5-star teachers. No contracts or sales calls",
  );
  let hrefLangUrls = [];
  for (let supportedLocale of SUPPORTED_LOCALES) {
    let langUrl = pathToUrl("/", supportedLocale);
    // @ts-ignore
    hrefLangUrls.push({
      rel: "alternate",
      hreflang: supportedLocale,
      href: langUrl,
    });
  }

  // both dimensions must be > 200 for Facebook to accept as
  // link preview
  const previewImageWidth = 2 * MAIN_IMAGE_WIDTH;
  const previewImageHeight = 2 * MAIN_IMAGE_HEIGHT;

  const imageUrl = getImageUrl(HOMEPAGE_PREVIEW_IMAGE, {
    w: previewImageWidth,
    h: previewImageHeight,
    fit: "clip",
  });

  return (
    <Helmet>
      <title>{title}</title>
      <meta property="og:title" content={title} />
      <meta name="description" content={description.slice(0, 155)} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      {hrefLangUrls.map((link, index) => (
        <link
          key={index}
          rel={link.rel}
          hrefLang={link.hreflang}
          href={link.href}
        />
      ))}
    </Helmet>
  );
}
